import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import type { LegalDoc } from "../legalDocs.ts";

export const LegalPageLayout = ({
  docEn,
  docHe,
  initialLang = "en",
}: {
  docEn: LegalDoc;
  docHe: LegalDoc;
  initialLang?: "en" | "he";
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const langParam = searchParams.get("lang");
  const [lang, setLang] = useState<"en" | "he">(
    langParam === "he" ? "he" : initialLang,
  );

  useEffect(() => {
    if (langParam === "he" || langParam === "en") {
      setLang(langParam);
    }
  }, [langParam]);

  const toggleLang = () => {
    const nextLang = lang === "en" ? "he" : "en";
    setLang(nextLang);
    setSearchParams(nextLang === "he" ? { lang: "he" } : {});
  };

  const isHe = lang === "he";
  const doc = isHe ? docHe : docEn;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
      <div className="container mx-auto px-4 py-12" dir={isHe ? "rtl" : "ltr"}>
        <Link
          to="/"
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          {isHe
            ? (
              <>
                <ArrowRight className="ml-2" /> חזרה לדף הבית
              </>
            )
            : (
              <>
                <ArrowLeft className="mr-2" /> Back to Home
              </>
            )}
        </Link>

        <div className="glass-card p-8 max-w-4xl mx-auto">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-3xl font-bold">{doc.title}</h1>
            <button
              type="button"
              onClick={toggleLang}
              className="px-3 py-1.5 rounded-lg border border-purple-500/40 text-purple-400 hover:bg-purple-500/20 hover:text-white transition-all text-sm font-medium"
            >
              {isHe ? "English" : "עברית"}
            </button>
          </div>

          <p className="text-gray-400 mb-2">
            {isHe
              ? `בתוקף מיום: ${doc.effectiveDate}`
              : `Effective Date: ${doc.effectiveDate}`}
          </p>

          {isHe && (
            <div className="p-3 mb-8 rounded bg-purple-950/30 border border-purple-800/40 text-xs text-purple-200">
              תרגום זה מובא מטעמי נוחות בלבד. הנוסח המקורי באנגלית הוא הנוסח
              המחייב מבחינה משפטית.
            </div>
          )}

          <div className="space-y-8 text-gray-300">
            {doc.sections.map((sec) => (
              <section key={sec.heading}>
                <h2 className="text-xl font-semibold mb-4 text-white">
                  {sec.heading}
                </h2>
                {sec.paragraphs.map((p, idx) => (
                  <p key={idx} className="mb-4 text-gray-300 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            <div className="pt-8 flex justify-center">
              <a
                href="https://wa.me/972504244206"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-all hover:scale-105"
              >
                {isHe ? "חזרה לצ'אט ב-WhatsApp" : "Open Chat in WhatsApp"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
